import { Badge, Chip } from '@vnetwork/ui/primitives';
import { type ColumnDef } from '@vnetwork/ui/data';
import { EntityLink } from '@/components/entity-link';
import { TableLink } from '@/components/table-link';
import { formatDate, formatMoney } from '@/lib/format';
import type { RevenueCombinedRow } from '@/api/customer-revenue';

export type OverviewGroupRow = {
  name: string;
  allocated: number;
  actual: number;
  projected: number;
};

/** Flat detail columns for the revenue-combined table. */
export const detailColumns: ColumnDef<RevenueCombinedRow, unknown>[] = [
  {
    id: 'revenue_month',
    header: 'Month',
    cell: ({ row }) => row.original.revenue_month ?? '—',
  },
  {
    id: 'product_group_service',
    header: 'Product Group / Service',
    cell: ({ row }) => {
      const r = row.original;
      if (r.service_id) {
        return (
          <EntityLink
            to={`/services/${r.service_id}`}
            id={r.service_id}
            name={r.product_group_name ?? r.service_name}
          />
        );
      }
      return r.product_group_name ?? '—';
    },
  },
  {
    id: 'invoice_id',
    header: 'Invoice',
    cell: ({ row }) => {
      const inv = row.original.invoice_id;
      if (!inv) return '—';
      return <TableLink to={`/invoices/${inv}`}>{inv}</TableLink>;
    },
  },
  {
    id: 'is_allocated',
    header: 'Allocated',
    cell: ({ row }) => {
      const v = row.original.is_allocated;
      if (v == null) return '—';
      return (
        <Chip tone={v ? 'success' : 'danger'} appearance="subtle">
          {v ? 'Allocated' : 'Not Allocated'}
        </Chip>
      );
    },
  },
  {
    id: 'allocated_amount',
    header: () => <div style={{ textAlign: 'right' }}>Allocated Amt</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>
        {formatMoney(row.original.allocated_amount ?? row.original.revenue_amount ?? null)}
      </div>
    ),
  },
  {
    id: 'total_amount',
    header: () => <div style={{ textAlign: 'right' }}>Total</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>{formatMoney(row.original.total_amount ?? null)}</div>
    ),
  },
  {
    id: 'is_projected',
    header: 'Type',
    cell: ({ row }) => {
      const r = row.original;
      const isProjected = !r.invoice_id || r.is_projected;
      return (
        <Badge tone={isProjected ? 'warning' : 'success'}>
          {isProjected ? 'Projected' : 'Actual'}
        </Badge>
      );
    },
  },
  {
    id: 'is_paid',
    header: 'Payment',
    cell: ({ row }) => {
      const r = row.original;
      if (r.is_projected || !r.invoice_id) return null;
      return (
        <Badge tone={r.is_paid ? 'success' : 'danger'}>
          {r.is_paid ? 'Paid' : 'Unpaid'}
        </Badge>
      );
    },
  },
  {
    id: 'payment_date',
    header: 'Payment Date',
    cell: ({ row }) => {
      const r = row.original;
      if (!r.is_paid || !r.payment_date) return '—';
      return formatDate(r.payment_date);
    },
  },
];

/** Product-group pivot columns for the Overview grouped view. */
export const overviewColumns: ColumnDef<OverviewGroupRow, unknown>[] = [
  {
    id: 'name',
    header: 'Product Group',
    cell: ({ row }) => row.original.name,
  },
  {
    id: 'allocated',
    header: () => <div style={{ textAlign: 'right' }}>Allocated</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>{formatMoney(row.original.allocated)}</div>
    ),
  },
  {
    id: 'actual',
    header: () => <div style={{ textAlign: 'right' }}>Actual Revenue</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>{formatMoney(row.original.actual)}</div>
    ),
  },
  {
    id: 'projected',
    header: () => <div style={{ textAlign: 'right' }}>Projected</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>{formatMoney(row.original.projected)}</div>
    ),
  },
];
